import React from "react";
import { useAuth } from "store/AuthProvider";
import { useData } from "store/DataProvider";
import useDataController from "hooks/useDataController";
import { useNetworkStatus } from "./useNetworkStatus";
import { usePendingQueue } from "./usePendingQueue";
import { authApi } from "services/api/auth";
import { stationApi } from "services/api/stations";
import { enqueueSnackbar } from "notistack";
import { StatusCode, StatusValue } from "services/api/flagbags/types";

interface ApiError {
    errorCode: number;
    errorMsg: string;
}

const useLoginController = () => {
    const auth = useAuth();
    const data = useData();
    const { isOnline } = useNetworkStatus();
    const { getPendingDataInfo } = usePendingQueue();
    const { updateFlagBagAction } = useDataController();

    /**
     * Resume offline session with cached user data
     * Loads cached station data and pending queue for offline operation
     */
    const handleResumeOfflineSession = async (cachedUser: { id: string; name: string }): Promise<boolean> => {
        try {
            // Resume the offline session in auth
            auth.resumeOfflineSession(cachedUser);
            
            // Get pending data info
            const pendingInfo = await getPendingDataInfo();
            
            // Show success message with pending count if applicable
            if (pendingInfo.pendingCount > 0) {
                enqueueSnackbar(
                    `未檢測到互聯網。正在恢復 ${cachedUser.name} 的緩存數據會話。` +
                    `您有 ${pendingInfo.pendingCount} 個待同步的旗袋包。`,
                    { variant: "info", autoHideDuration: 5000 }
                );
            } else {
                enqueueSnackbar(
                    `未檢測到互聯網。正在恢復 ${cachedUser.name} 的緩存數據會話。`,
                    { variant: "info", autoHideDuration: 3000 }
                );
            }
            
            return true;
        } catch (error) {
            console.error("Failed to resume offline session:", error);
            enqueueSnackbar("恢復緩存會話失敗", { variant: "error" });
            return false;
        }
    };

    const handleLogin = async (eventId: string, username: string, password: string): Promise<boolean> => {
        const pendingInfo = await getPendingDataInfo();
        const cachedUser = auth.getLastCachedUser();

        // A3 & A4: Offline Mode - prevent new logins, only allow resuming cached session
        if (!isOnline) {
            if (!cachedUser) {
                // A3: No cached data available
                enqueueSnackbar("無法登入：沒有互聯網連接。此設備沒有可用的緩存數據。", { variant: "error" });
                return false;
            }

            // A4: Cached session exists - should use resume button instead
            enqueueSnackbar(`離線模式下請點擊"恢復 ${cachedUser.name} 的會話"按鈕。`, { variant: "warning" });
            return false;
        }

        /**
         * A2: Online Mode - Block login if different user has pending data
         */
        if (pendingInfo.pendingCount > 0) {
            if (pendingInfo.userName !== username) {
                enqueueSnackbar(
                    `上一個用戶 [${pendingInfo.userName}] 有 ${pendingInfo.pendingCount} 個未同步的旗袋包。` +
                    `請要求他們登入以完成同步。`,
                    { variant: "error", persist: false }
                );
                return false;
            }
        }

        try {
            auth.setEventIdValue(eventId);
            const loginResponse = await authApi.login(eventId, username.trim().toLowerCase(), password);
            auth.login(loginResponse);
            const userInfo = await authApi.getUserInfo(eventId);
            auth.setUserNameValue(userInfo.name);
            auth.setUserID(userInfo.id);
            
            // Cache user info and eventId for offline session resumption
            localStorage.setItem("userInfo", JSON.stringify({
                id: userInfo.id,
                name: userInfo.name,
                email: username,
                eventId: eventId
            }));
            // Fetch station overview after successful login
            if (eventId) {
                try {
                    const overview = await stationApi.getOverview(eventId);
                    
                    // setStationOverviewData will trigger useEffect in DataProvider to set stationId automatically
                    data.setStationOverviewData(overview);
                    
                    // Safely access stations array with validation
                    if (overview?.stations && overview.stations.length > 0) {
                        const firstStation = overview.stations[0];
                        data.setFlagBagCount(firstStation.flagbags?.length || 0);
                        
                        if (overview.event?.id) {
                            data.setEventId(overview.event.id);
                        }
                        
                        data.setFlagBagPreassign(
                            firstStation.flagbags?.filter((flagbag) => flagbag.status === StatusValue[StatusCode.ASSIGNED]).length || 0
                        );
                    } else {
                        data.setFlagBagCount(0);
                        data.setFlagBagPreassign(0);
                    }
                } catch (error) {
                    console.error("Failed to fetch station overview:", error);
                    // Failed to fetch station overview, but continue with login
                }
            }

            // Try to update flag bag data if stationID is available
            const stationID = data.getStationId();
            if (stationID) {
                try {
                    await updateFlagBagAction(stationID);
                } catch (error) {
                    console.error("Failed to update flag bag data:", error);
                    // Failed to update flag bag data, continuing with login
                }
            }

            // Show success message
            if (pendingInfo.pendingCount > 0 && username === pendingInfo.userName) {
                enqueueSnackbar(`歡迎回來，${userInfo.name}！請前往同步頁面完成您 ${pendingInfo.pendingCount} 項待處理操作。`, { variant: "success" });
            } else {
                enqueueSnackbar(`登入成功`, { variant: "success" });
            }
            return true;
        } catch (error) {
            console.error("Login failed:", error);
            const apiError = error as ApiError;
            enqueueSnackbar(apiError.errorMsg || "登入失敗", { variant: "error" });
            return false;
        }
    }

    return { handleLogin, handleResumeOfflineSession, isOnline };
};

export default useLoginController;
